// Projects Block View Component (Read-only)
import { useState, useEffect } from 'react';
import { FolderOpen } from 'lucide-react';

const ProjectsBlockView = ({ content, userId }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selectedProjects = content?.selectedProjects || [];

  // Helper to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color || color === 'transparent') return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 186;
  };

  const getTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-900' : 'text-white';
  };

  const getSubTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-700' : 'text-gray-100';
  };

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  useEffect(() => {
    if (selectedProjects.length > 0) {
      loadProjectDetails();
    } else {
      setLoading(false);
    }
  }, []);

  const loadProjectDetails = async () => {
    try {
      setLoading(true);
      // Fetch full project details for selected projects
      const projectsData = await Promise.all(
        selectedProjects.map(async (selectedProject) => {
          try {
            const response = await fetch(`/api/projects/public/${userId}/${selectedProject.projectId}`);
            if (!response.ok) {
              console.error(`Failed to fetch project ${selectedProject.projectId}`);
              return null;
            }
            const data = await response.json();
            return {
              ...data,
              customHeadline: selectedProject.projectHeadline
            };
          } catch (err) {
            console.error(`Error fetching project ${selectedProject.projectId}:`, err);
            return null;
          }
        })
      );

      // Filter out any failed fetches
      setProjects(projectsData.filter(p => p !== null));
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (selectedProjects.length === 0) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="w-16 h-16 text-[#7A99D8] mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">No projects to display</p>
      </div>
    );
  }

  return (
    <>
      {/* Title */}
      {content?.title && (
        <h3
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
          style={{ fontFamily: getFontFamily(content?.titleFontFamily) }}
        >
          {content.title}
        </h3>
      )}

      <div className="space-y-8">
        {projects.map((project, index) => {
          const selectedProject = selectedProjects.find(sp => sp.projectId === project.id);
          const cardBg = content?.cardBackgroundColor || '#ffffff';
          const headlineBg = content?.headlineBackgroundColor || '#dbeafe';
          const displayDescription = selectedProject?.useCustomDescription
            ? selectedProject?.customDescription
            : project.description;

          return (
            <div
              key={project.id}
              className="rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
              style={{ backgroundColor: cardBg }}
            >
            <div className="p-6">
              {/* Project Image - floated left on desktop */}
              {project.main_image_url && (
                <img
                  src={project.main_image_url}
                  alt={project.name}
                  className="w-full h-48 md:w-[300px] md:h-[300px] object-cover md:float-left md:mr-6 md:mb-4 mb-4 rounded"
                />
              )}

              {/* Custom Headline - NOW FIRST */}
              {selectedProject?.projectHeadline && (
                <div
                  className="mb-4 p-4 rounded-lg border-l-4 border-[#7A99D8]"
                  style={{
                    backgroundColor: headlineBg,
                    fontFamily: getFontFamily(content?.headlineFontFamily)
                  }}
                >
                  <div
                    className={`leading-relaxed ${getTextColorClass(headlineBg)}`}
                    dangerouslySetInnerHTML={{ __html: selectedProject.projectHeadline }}
                  />
                </div>
              )}

              {/* Project Name - NOW SECOND (removed number badge) */}
              <h4
                className={`text-lg font-bold mb-2 ${getTextColorClass(cardBg)}`}
                style={{ fontFamily: getFontFamily(content?.bodyFontFamily) }}
              >
                {project.name}
              </h4>

              {/* Project Description (or custom description) */}
              {displayDescription && (
                <div
                  className={`mb-4 leading-relaxed ${getSubTextColorClass(cardBg)} whitespace-pre-wrap`}
                  style={{ fontFamily: getFontFamily(content?.bodyFontFamily) }}
                >
                  {displayDescription}
                </div>
              )}

              {/* Clear float */}
              <div className="clear-both"></div>
            </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default ProjectsBlockView;
